import React, { useState, useRef } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, Container, Typography, Snackbar, Alert } from '@mui/material';

import Cookie, { CookieAttributes } from 'js-cookie';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';

import { RHFTextField, FormProvider } from 'globals/components/hook-form';

import Page from 'globals/components/Page';

import { makeAuth } from './infra/services/auth';

const dataTreatment = (obj: any) => {
  let nickName = obj.name.split(' ')[0].toLowerCase();

  nickName = nickName.charAt(0).toUpperCase() + nickName.slice(1);

  return { ...obj, nickName };
};

export function Auth() {
  const methods = useForm();
  const _reCaptchaRef = useRef();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);

  const [visibility, setVisibility] = useState(true);
  const [, setState] = useState({
    username: { error: false },
    password: { error: false },
  });

  const handleValidate = () => {
    setState({
      username: { error: false },
      password: { error: false },
    });

    if (!username) {
      setState((state) => ({
        ...state,
        username: { error: true },
      }));
      throw 'Preencha o usuário';
    }

    if (!password) {
      setState((state) => ({
        ...state,
        password: { error: true },
      }));
      throw 'Preencha a senha';
    }
  };

  const handleExpired = () => {
    _reCaptchaRef.current.reset();
  };

  const getCaptchaToken = async () => {
    let curToken = _reCaptchaRef.current.getValue();

    if (!curToken) {
      curToken = await _reCaptchaRef.current.executeAsync();
    }

    if (!curToken) {
      _reCaptchaRef.current.reset();
      setOpen(true);
      setMessage('Ops, algo deu errado. Tente novamente');
      return;
    }

    return curToken;
  };

  const signIn = async (token?: string) => {
    const body = JSON.stringify({
      username,
      password,
      'g-recaptcha-response': token,
    });

    const { data } = await makeAuth(body);

    const cookieConfig: CookieAttributes = {
      domain: '.withub.ai',
      expires: data.data.expires_in,
      path: '/',
      secure: false,
    };

    Cookie.set('token_type', data.data.token_type, cookieConfig);
    Cookie.set('access_token', data.data.access_token, cookieConfig);

    setOpen(true);
    setSuccess(true);
    setMessage(`Seja bem-vindo, ${dataTreatment(data.data).nickName}!`);

    const referer = Cookie.get('referer');

    setTimeout(() => {
      window.location.href = (referer || process.env.NEXT_PUBLIC_DEFAULT_REFERER) as string;
    }, 500);
  };

  const handleSubmit: any = async (event: any) => {
    try {
      event.preventDefault();
      setLoading(true);
      handleValidate();

      const token = await getCaptchaToken();
      await signIn(token);
    } catch (error: any) {
      setSuccess(false);
      setOpen(true);
      setLoading(false);

      if (error) {
        setMessage(error.toString());
        return;
      }

      setMessage('Ops, algo deu errado!');
    }
  };

  return (
    <Page title="Login">
      <Container maxWidth={'md'}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          sx={{ height: '100vh', padding: [0, 0, 30] }}
        >
          <FormProvider onSubmit={handleSubmit} methods={methods}>
            <Box display="flex" flexDirection="column" alignItems="center" sx={{ gap: 1 }}>
              <svg width="50" height="45" viewBox="0 0 50 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0 11.6693L5.71564 16.4297C7.84336 13.8972 10.4528 11.8056 13.3948 10.2743C16.3369 8.74304 19.554 7.80209 22.8624 7.5052C26.1708 7.20831 29.5057 7.5613 32.6767 8.54401C35.8477 9.52672 38.7926 11.1199 41.3433 13.2326C42.047 13.8154 42.7186 14.4355 43.3552 15.0899V15.0978C43.6674 15.4812 44.0534 15.7989 44.4908 16.0325C44.9281 16.2661 45.4079 16.4109 45.9022 16.4583C46.3965 16.5058 46.8954 16.455 47.3697 16.3089C47.844 16.1628 48.2842 15.9244 48.6646 15.6075C49.0451 15.2906 49.3581 14.9016 49.5854 14.4633C49.8127 14.025 49.9498 13.5461 49.9886 13.0546C50.0274 12.5631 49.9671 12.0689 49.8114 11.6008C49.6556 11.1327 49.4074 10.7001 49.0813 10.3282L49.0313 10.2772C48.9487 10.1818 48.8608 10.0911 48.768 10.0054C47.9303 9.13886 47.0455 8.31852 46.1175 7.54803C39.4504 2.02604 30.8472 -0.641781 22.1994 0.131031C13.5515 0.903843 5.56659 5.05403 0 11.6693"
                  fill="url(#paint0_linear_660_1014)"
                />
                <path
                  d="M33.1181 19.96C40.1241 19.96 45.1682 23.3349 45.1682 29.8063V43.4928C45.1682 43.9555 45.0695 44.1098 44.3782 44.2156L39.9806 44.9999C39.4763 44.9999 39.1906 44.7685 39.1906 44.3071V29.6207C39.1906 26.6144 36.9746 24.8132 32.939 24.8132C31.0508 24.7774 29.1796 25.1728 27.4696 25.9687C27.9524 27.1589 28.1996 28.4304 28.1977 29.7135V43.2641C28.1977 43.8052 27.9344 43.9725 27.3564 44.1098L23.0746 44.9934C22.5703 44.9934 22.2345 44.762 22.2345 44.3006V29.6207C22.2345 26.2458 20.3293 24.7674 16.6861 24.7674C15.0087 24.7576 13.3351 24.9281 11.6946 25.2759V43.5033C11.6946 43.966 11.4313 44.098 10.8533 44.2261L6.54121 44.9947C5.98031 44.9947 5.7003 44.7637 5.70117 44.3019V22.5951C5.70117 22.2251 5.86971 21.9415 6.37399 21.7624C9.62479 20.5907 13.0539 19.981 16.5123 19.96C19.3715 19.8717 22.1949 20.6106 24.6388 22.0866C27.2274 20.6634 30.1434 19.9307 33.1023 19.96"
                  fill="#3482FF"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_660_1014"
                    x1="0"
                    y1="8.23779"
                    x2="50"
                    y2="8.23779"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#30DBF2" />
                    <stop offset="0.18" stopColor="#30DBF2" />
                    <stop offset="0.259" stopColor="#32DAEC" />
                    <stop offset="0.359" stopColor="#3ADADC" />
                    <stop offset="0.471" stopColor="#46D9C2" />
                    <stop offset="0.591" stopColor="#58D79E" />
                    <stop offset="0.718" stopColor="#6ED56F" />
                    <stop offset="0.849" stopColor="#8AD337" />
                    <stop offset="0.88" stopColor="#91D329" />
                    <stop offset="1" stopColor="#91D329" />
                  </linearGradient>
                </defs>
              </svg>
              <Typography variant="h5" component="h1" textAlign="center">
                Bem-vindo de volta, <br />
                entre na sua conta
              </Typography>
              <RHFTextField
                onChange={({ target }) => setUsername(target.value)}
                value={username}
                name="username"
                label="Usuário"
                onKeyDown={() => {
                  setState({
                    username: { error: false },
                    password: { error: false },
                  });
                }}
              />
              <Box width="100%">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <RHFTextField
                    onChange={({ target }) => setPassword(target.value)}
                    value={password}
                    type={visibility ? 'password' : 'text'}
                    name="password"
                    label="Senha"
                    onKeyDown={() => {
                      setState({
                        username: { error: false },
                        password: { error: false },
                      });
                    }}
                  />
                  {visibility ? (
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => setVisibility(!visibility)}
                      style={{
                        position: 'absolute',
                        margin: 'auto',
                        marginLeft: '-20px',
                        cursor: 'pointer',
                      }}
                    >
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    </svg>
                  ) : (
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => setVisibility(!visibility)}
                      style={{
                        position: 'absolute',
                        margin: 'auto',
                        marginLeft: '-20px',
                        cursor: 'pointer',
                      }}
                    >
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                    </svg>
                  )}
                </Box>
              </Box>
              {process.env.NEXT_PUBLIC_RECAPTCHA_KEY && (
                <ReCAPTCHA
                  ref={_reCaptchaRef}
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}
                  onExpired={handleExpired}
                  asyncScriptOnLoad={() => setLoading(false)}
                  size="invisible"
                />
              )}
              <LoadingButton
                variant="outlined"
                type="submit"
                disabled={loading}
                loading={loading}
                sx={{ width: '100%' }}
              >
                Entrar
              </LoadingButton>
              <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={open}
                autoHideDuration={4000}
                onClose={() => setOpen(false)}
              >
                <Alert severity={success ? 'success' : 'error'} sx={{ width: '100%' }}>
                  {success ? `😃 ${message}` : message}
                </Alert>
              </Snackbar>
            </Box>
          </FormProvider>
        </Box>
      </Container>
    </Page>
  );
}
